import { FunctionComponent, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Modal } from "@/components/Modals/Modal";
import { TextArea } from "@/components/TextArea/TextArea";

const StrengthReasonSchema = z.object({
	feedback: z.string().trim().min(3, "feed.runCard.strength.reasonRequired"),
});

export type StrengthReasonFormValues = z.infer<typeof StrengthReasonSchema>;

interface StrengthReasonModalProps {
	isOpened: boolean;
	isSubmitting?: boolean;
	onClose: () => void;
	onSubmit: (values: StrengthReasonFormValues) => Promise<void> | void;
}

/**
 * Optional reason of a strength quick-change (D1) — POSTs feedback
 * `SCORE_CHANGE`, which stores the reason without touching the priority.
 * Skipping it (closing the modal) is fine by design.
 */
export const StrengthReasonModal: FunctionComponent<StrengthReasonModalProps> = ({
	isOpened,
	isSubmitting = false,
	onClose,
	onSubmit,
}) => {
	const { t } = useTranslation();
	const {
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors, isValid },
	} = useForm<StrengthReasonFormValues>({
		defaultValues: { feedback: "" },
		mode: "onChange",
		resolver: zodResolver(StrengthReasonSchema),
	});

	useEffect(() => {
		if (!isOpened) {
			reset();
		}
	}, [isOpened, reset]);

	return (
		<Modal
			disableSave={!isValid || isSubmitting}
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isSubmitting}
			isOpened={isOpened}
			size="sm"
			submitButtonText={t("feed.runCard.strength.reasonSubmit")}
			subtitle={t("feed.runCard.strength.reasonDescription")}
			title={t("feed.runCard.strength.reasonTitle")}
		>
			<div className="flex flex-col gap-2">
				<TextArea
					error={errors.feedback?.message ? t(errors.feedback.message) : undefined}
					handleChange={(event) =>
						setValue("feedback", event.target.value, { shouldDirty: true, shouldValidate: true })
					}
					label={t("feed.runCard.strength.reasonLabel")}
					name="feedback"
					placeholder={t("feed.runCard.strength.reasonPlaceholder")}
					showError={Boolean(errors.feedback)}
					value={watch("feedback")}
				/>
			</div>
		</Modal>
	);
};
